import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { MACHINES_COMMAND_STATUSES } from '@/constants/app/machines';
import { useSendBulkMachineCommandsMutation } from '@/store/api/services/machines/machines';

import type { CommandResult } from '@/types';
import { ClickableText, type ModalChildrenProps } from '@bitstopco/bitstop-theme';

const BulkSetMachineStatus = ({ hide, props }: ModalChildrenProps) => {
  const [sendBulkCommand, { isLoading, isSuccess }] = useSendBulkMachineCommandsMutation();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [results, setResults] = useState<CommandResult[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSuccess && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isSuccess]);

  const handleSubmit = async () => {
    if (!selectedStatus) {
      toast.error('Please select a machine status.');
      return;
    }

    try {
      const { data } = await sendBulkCommand({
        atm_ids: props?.selectedAtms.map(({ id }) => id) || [],
        command: 'SetStatus',
        machine_status: selectedStatus,
      }).unwrap();

      const results = data?.results || [];
      setResults(results);
      const successCount = results.filter((res: any) => res.success).length;

      if (successCount === results.length) {
        toast.success(
          `Set status "${MACHINES_COMMAND_STATUSES[selectedStatus]?.label || selectedStatus}" on ${successCount} machine(s).`,
        );
      } else {
        const atmErrList = results
          .filter((atm: any) => atm.error)
          .map((atm: any) => `${atm.atm_id}`);
        toast.error(
          `${results.length - successCount} machine(s) failed. [${atmErrList.join(', ')}]`,
        );
      }
    } catch (err) {
      toast.error(`Unexpected error: ${err}`);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 2 }} ref={contentRef}>
        <Typography variant="h6" mb={2}>
          Set Machine Status
        </Typography>

        <Box mb={2}>
          <Typography variant="subtitle1">Select Status</Typography>
          <Select
            fullWidth
            displayEmpty
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            disabled={isLoading}
            renderValue={(selected) => {
              const status = MACHINES_COMMAND_STATUSES[selected];
              if (!status) {
                return <Typography color="text.disabled">Select a status</Typography>;
              }

              return (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: (theme) => theme.palette[status.color || 'default']?.main || 'grey',
                    }}
                  />
                  {status.label}
                </Box>
              );
            }}
          >
            <MenuItem disabled value="">
              <em>Select a status</em>
            </MenuItem>

            {Object.entries(MACHINES_COMMAND_STATUSES).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: (theme) => theme.palette[value.color || 'default']?.main || 'grey',
                    }}
                  />
                  {value.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>

        {!isSuccess ? (
          <>
            <Typography variant="subtitle1" mb={1}>
              Affected ATM IDs:
            </Typography>
            <Box position="relative">
              <TableContainer sx={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ATM ID</TableCell>
                      <TableCell>ATM Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {props?.selectedAtms?.map((atm: any) => (
                      <TableRow key={atm.id}>
                        <TableCell>
                          <ClickableText
                            fixIcon
                            text={atm.id.toString()}
                            clipboardValue={atm.id.toString()}
                            name="Machine ID"
                            to={`${window.location.origin}/machine/${atm.id}`}
                          />
                        </TableCell>
                        <TableCell>{atm.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {isLoading && (
                <CircularProgress
                  size={40}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            </Box>
          </>
        ) : (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" mb={2}>
              Status Change Results
            </Typography>
            <Box position="relative">
              <TableContainer sx={{ opacity: isLoading ? 0.5 : 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ATM ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map(({ atm_id, success, error }: any) => (
                      <TableRow key={atm_id}>
                        <TableCell>
                          <ClickableText
                            fixIcon
                            text={atm_id.toString()}
                            clipboardValue={atm_id.toString()}
                            name="Machine ID"
                            to={`${window.location.origin}/machine/${atm_id}`}
                          />
                        </TableCell>
                        <TableCell>{success ? 'Success' : 'Failed'}</TableCell>
                        <TableCell>{error || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {isLoading && (
                <CircularProgress
                  size={40}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        {!isSuccess && (
          <>
            <Button onClick={hide}>Cancel</Button>
            <LoadingButton variant="contained" onClick={handleSubmit} loading={isLoading}>
              Apply Status
            </LoadingButton>
          </>
        )}
        {isSuccess && (
          <Button fullWidth onClick={hide}>
            Back to Machines
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default BulkSetMachineStatus;
